// Shared notification display utilities and query keys — used by both web and mobile apps
/** Poll interval for unread notification count — balances freshness against API load. */
export const NOTIFICATION_REFETCH_MS = 30_000;
/** TanStack Query key factory for notification-related queries. */
export const notificationKeys = {
    list: (filters) => ['notifications', 'list', filters],
    /** Prefix matching every flat-list query regardless of filter. Needed because
     *  `list()` is `['notifications', 'list', undefined]`, and TanStack compares
     *  key elements positionally — so it would NOT match a query registered with
     *  real filters, which is what the bell and the Mentions panels use. */
    listRoot: () => ['notifications', 'list'],
    /** Infinite query behind the project-grouped list. */
    groups: (filters) => ['notifications', 'groups', filters],
    /** Prefix matching every grouped-list query regardless of filter. Note that
     *  `groups({})` would NOT match `groups({type:'mention'})` — TanStack
     *  compares key elements positionally, so a filter-agnostic invalidate has
     *  to stop before the filter slot. */
    groupsRoot: () => ['notifications', 'groups'],
    /** One expanded group's full notification list. */
    groupItems: (projectId, filters) => ['notifications', 'group-items', projectId ?? 'general', filters],
    groupItemsRoot: () => ['notifications', 'group-items'],
    unreadCount: () => ['notifications-unread-count'],
    preferences: () => ['notification-preferences'],
};
/** Label for the bucket holding notifications that concern no project. */
export const GENERAL_NOTIFICATION_GROUP_LABEL = 'General';
/** Notifications shown inline in a group before the "Show N more" expander. */
export const NOTIFICATION_GROUP_PREVIEW_COUNT = 3;
/** Project groups fetched per page of the infinite list. */
export const NOTIFICATION_GROUP_PAGE_SIZE = 10;
/** Map notification type to a short icon letter. */
export const NOTIFICATION_TYPE_ICONS = {
    // Task lifecycle
    task_assigned: 'T',
    task_overdue: '!',
    task_completed: 'C',
    task_reopened: 'R',
    task_at_risk: '!',
    task_created: 'T',
    task_updated: 'T',
    task_deleted: 'T',
    task_reassigned: 'T',
    task_unassigned: 'T',
    task_ready_to_start: 'S',
    task_status_changed: 'T',
    deadline_reminder: 'D',
    // Document lifecycle
    document_shared: 'D',
    document_updated: 'D',
    document_uploaded: 'D',
    document_version_uploaded: 'D',
    // Notes & comments
    note_created: 'N',
    comment_reply: 'R',
    mention: '@',
    // Project chat
    project_message_posted: 'M',
    project_message_mention: '@',
    // RBAC
    role_changed: 'R',
    // Project lifecycle
    project_member_added: 'P',
    project_member_removed: 'P',
    project_created: 'P',
    project_updated: 'P',
    project_archived: 'P',
    project_deleted: 'P',
    // Escalations
    escalation_alert: 'E',
    // User lifecycle
    user_invited: 'U',
    user_deactivated: 'U',
    user_reactivated: 'U',
};
/** Map notification type to a human-readable label. */
export const NOTIFICATION_TYPE_LABELS = {
    // Task lifecycle
    task_assigned: 'Task Assigned',
    task_overdue: 'Task Overdue',
    task_completed: 'Task Completed',
    task_reopened: 'Task Reopened',
    task_at_risk: 'Task At Risk',
    task_created: 'Task Created',
    task_updated: 'Task Updated',
    task_deleted: 'Task Deleted',
    task_reassigned: 'Task Reassigned',
    task_unassigned: 'Task Unassigned',
    task_ready_to_start: 'Ready to Start',
    task_status_changed: 'Status Changed',
    deadline_reminder: 'Deadline Reminder',
    // Document lifecycle
    document_shared: 'Document Shared',
    document_updated: 'Document Updated',
    document_uploaded: 'Document Uploaded',
    document_version_uploaded: 'New Version',
    // Notes & comments
    note_created: 'Note Created',
    comment_reply: 'Message Reply',
    mention: 'Mention',
    // Project chat
    project_message_posted: 'Project Message',
    project_message_mention: 'Mentioned in Chat',
    // RBAC
    role_changed: 'Role Changed',
    // Project lifecycle
    project_member_added: 'Added to Project',
    project_member_removed: 'Removed from Project',
    project_created: 'Project Created',
    project_updated: 'Project Updated',
    project_archived: 'Project Archived',
    project_deleted: 'Project Deleted',
    // Escalations
    escalation_alert: 'Escalation',
    // User lifecycle
    user_invited: 'User Invited',
    user_deactivated: 'User Deactivated',
    user_reactivated: 'User Reactivated',
};
/** Notification type groups for filter chips. */
export const NOTIFICATION_FILTER_GROUPS = [
    // Mentions first — the dedicated Mentions tab owns this group; UIs that render
    // in-tab filter chips exclude it (see MENTIONS_GROUP_LABEL) so there is one
    // prominent path to mentions rather than a tab and a duplicate chip.
    // Both mention types live here, and both are in ALWAYS_ON_NOTIFICATION_TYPES.
    // Adding a muteable type to this group would flip the group's `alwaysOn` to
    // false and make plain `mention` muteable as a side effect.
    { label: 'Mentions', types: ['mention', 'project_message_mention'] },
    {
        label: 'Tasks',
        types: [
            'task_assigned',
            'task_created',
            'task_completed',
            'task_reopened',
            'task_overdue',
            'task_updated',
            'task_status_changed',
            'task_deleted',
            'task_reassigned',
            'task_unassigned',
            'task_ready_to_start',
            'task_at_risk',
        ],
    },
    {
        label: 'Documents',
        types: [
            'document_shared',
            'document_uploaded',
            'document_version_uploaded',
            'document_updated',
        ],
    },
    // Project chat folds in here rather than earning its own filter chip in both
    // clients — it is the same "someone wrote something" category to a reader.
    { label: 'Notes', types: ['note_created', 'comment_reply', 'project_message_posted'] },
    {
        label: 'Projects',
        types: [
            'project_member_added',
            'project_member_removed',
            'project_created',
            'project_updated',
            'project_archived',
            'project_deleted',
        ],
    },
    {
        label: 'Team',
        types: ['role_changed', 'user_invited', 'user_deactivated', 'user_reactivated'],
    },
    { label: 'Alerts', types: ['escalation_alert', 'deadline_reminder'] },
];
/** The notification types shown under the dedicated Mentions tab, the home
 *  Mentions panel, and counted into the unread-count endpoint's mentionCount.
 *
 *  There are two: a plain `@username` mention (in a note or comment) and a
 *  project-chat mention (`project_message_mention`, includes `@all`). Both are
 *  in `ALWAYS_ON_NOTIFICATION_TYPES` (notification-settings.ts) and both are
 *  in the 'Mentions' entry of NOTIFICATION_FILTER_GROUPS above — those three
 *  lists are independent and must be kept in sync by hand; there is no single
 *  source to derive them from without an import cycle between this file and
 *  notification-settings.ts. A chat mention that reached only two of the
 *  three would notify the user but never surface in the tab, the panel, or
 *  the badge — exactly the bug this constant was widened to fix. */
export const MENTION_NOTIFICATION_TYPES = ['mention', 'project_message_mention'];
/** Label of the first-position group both apps exclude from in-tab filter chips
 *  (the Mentions tab owns it). */
export const MENTIONS_GROUP_LABEL = 'Mentions';
/** Rows the home-page Mentions panel previews before deferring to "View all".
 *  Shared so web and mobile cannot drift — the panels are meant to show the same
 *  thing on both platforms. The panel's badge still shows the true total. */
export const HOME_MENTIONS_PREVIEW_LIMIT = 3;
/** Format a date string as relative time (e.g., "2m ago", "3h ago"). */
export function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)
        return 'just now';
    if (mins < 60)
        return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)
        return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}
//# sourceMappingURL=notification-display.js.map